/**
 * What the guest is doing right now, live — and the approval that saves it.
 *
 * Approval is the one action a device can never take. In the legacy system a
 * tablet could emit `save_pdf_signature` and mint a signed legal document with
 * no human involved; here the button lives here, behind `sessions:approve`,
 * and the transition it triggers is a single conditional UPDATE so a
 * double-click cannot produce two archived copies.
 */

import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Ban, CheckCircle2, MessageSquare, RotateCcw, Send, Undo2 } from 'lucide-react'
import { api } from '@/lib/api'
import { on } from '@/lib/socket'
import type { SigningSession, ValidationIssue } from '@/lib/types'
import { Badge, Button, Card, Input, Separator, Spinner } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import {
  DocumentViewer,
  PageFitToggle,
  PageNavigator,
  usePageFit,
} from '@/components/domain/DocumentViewer'
import { ConfirmDestructive } from '@/components/domain/ConfirmDestructive'
import { PermissionGate, useAuth } from '@/lib/auth'
import { MirrorFields } from '@/features/workspace/MirrorFields'
import { useSessionValues } from '@/features/workspace/useSessionValues'
import { cn, formatRelative } from '@/lib/utils'

const STATUS_COPY: Record<
  string,
  { label: string; tone: 'brand' | 'info' | 'warning' | 'success' }
> = {
  draft: { label: 'Tablet chosen — not sent yet', tone: 'brand' },
  pushed: { label: 'Sent — waiting for the tablet', tone: 'brand' },
  active: { label: 'Guest is filling it in', tone: 'info' },
  awaiting_approval: {
    label: 'Signed & submitted — verify it',
    tone: 'warning',
  },
  saving: { label: 'Finalizing…', tone: 'info' },
  completed: { label: 'Finalized & locked', tone: 'success' },
}

export function SessionMirror({
  session,
  onChanged,
  onFinished,
}: {
  session: SigningSession
  onChanged: () => void
  onFinished: () => void
}) {
  const toast = useToast()
  const { session: auth } = useAuth()
  // A correction after the push is the same right as pushing in the first
  // place, and the API already enforces it on `PATCH /sessions/{id}/values`.
  const canEdit =
    Boolean(auth?.permissions.includes('sessions:manage')) &&
    ['draft', 'pushed', 'active'].includes(session.status)
  const values = useSessionValues(session, onChanged)
  const [page, setPage] = useState(1)
  const fit = usePageFit(session.pages.find((p) => p.page === page) ?? session.pages[0])
  const [message, setMessage] = useState('')
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [changed, setChanged] = useState<Set<string>>(new Set())
  const [issues, setIssues] = useState<ValidationIssue[]>([])
  const clearTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // Highlight a field for a moment when the guest changes it, so the
  // receptionist notices movement without re-reading the whole form.
  useEffect(() => {
    const off = on<{ field_key: string }>('session.field_changed', (payload) => {
      setChanged((current) => new Set(current).add(payload.field_key))
      const timers = clearTimers.current
      clearTimeout(timers.get(payload.field_key))
      timers.set(
        payload.field_key,
        setTimeout(() => {
          setChanged((current) => {
            const next = new Set(current)
            next.delete(payload.field_key)
            return next
          })
        }, 1400),
      )
      onChanged()
    })
    const offCheckbox = on('session.checkbox_changed', () => onChanged())
    const offSignature = on('session.signature_updated', () => onChanged())
    const offMessage = on('session.message', () => onChanged())
    // A change after the guest signed removes their signature on the server:
    // it was a signature of the old wording. Say so, rather than let the
    // "signed" count quietly drop.
    const offInvalidated = on('session.signatures_invalidated', () => {
      toast.info(
        'The guest will sign again',
        'A value changed after they signed, so that signature no longer stands.',
      )
      onChanged()
    })
    return () => {
      off()
      offCheckbox()
      offSignature()
      offMessage()
      offInvalidated()
    }
  }, [onChanged, toast])

  const approve = useMutation({
    // The digest of the submission on screen goes with the approval, so what
    // is finalized is exactly what this receptionist verified - not a
    // re-submission that arrived while they were reading.
    mutationFn: () =>
      api.post(
        `/sessions/${session.id}/approve`,
        session.submitted_digest ? { expected_digest: session.submitted_digest } : {},
        { idempotencyKey: session.id },
      ),
    onSuccess: () => {
      toast.info('Saving the signed document…')
      onChanged()
    },
    onError: (error: Error) => toast.error('Could not approve', error.message),
  })

  const reopen = useMutation({
    mutationFn: () => api.post(`/sessions/${session.id}/reopen`),
    onSuccess: () => {
      toast.info('Sent back to the guest', 'They can correct it and submit again.')
      onChanged()
    },
  })

  const cancel = useMutation({
    mutationFn: () => api.post(`/sessions/${session.id}/cancel`),
    onSuccess: () => {
      toast.success('Session cancelled', 'The tablet is back on signage.')
      onFinished()
    },
  })

  const send = useMutation({
    mutationFn: (body: string) => api.post(`/sessions/${session.id}/messages`, { body }),
    onSuccess: () => {
      setMessage('')
      onChanged()
    },
  })

  const push = useMutation({
    mutationFn: () => api.post(`/sessions/${session.id}/push`),
    onSuccess: () => {
      toast.success('Sent to the tablet')
      onChanged()
    },
  })

  useEffect(() => {
    if (session.status !== 'awaiting_approval') {
      setIssues([])
      return
    }
    void api
      .get<{ can_submit: boolean; issues: ValidationIssue[] }>(`/sessions/${session.id}/readiness`)
      .then((result) => setIssues(result.issues))
      .catch(() => setIssues([]))
  }, [session.id, session.status, session.seq])

  const status = STATUS_COPY[session.status] ?? {
    label: session.status,
    tone: 'brand' as const,
  }
  const signed = session.signatures.filter((signature) => signature.captured).length
  const awaiting = session.status === 'awaiting_approval'
  const saving = session.status === 'saving'

  const filledCount = useMemo(
    () => session.fields.filter((field) => field.value.trim()).length,
    [session.fields],
  )

  return (
    <Card className="overflow-hidden xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-[var(--border)] px-3 py-2">
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="truncate text-[15px] font-semibold">{session.document_type_label}</h2>
          <p className="truncate text-[12px] text-[var(--text-muted)]">
            {session.device?.name ?? 'No tablet'} · started {formatRelative(session.started_at)}
          </p>
        </div>
        <PageNavigator pages={session.pages} current={page} onChange={setPage} />
        <PageFitToggle mode={fit.mode} onChange={fit.setMode} />
        <div className="ms-auto flex items-center gap-2">
          <Badge tone={status.tone}>
            {saving && <Spinner className="h-3 w-3" />}
            {status.label}
          </Badge>
          <span className="tabular text-[12px] text-[var(--text-muted)]">
            {filledCount}/{session.fields.length} fields · {signed}/{session.signatures.length}{' '}
            signed
          </span>
        </div>
      </header>

      {/* Screen-tall at xl, like the review, and sized the same way: the page
          as wide as its column, or whole in a column exactly as wide as it. */}
      <div
        className={cn(
          'grid gap-3 p-3 xl:min-h-0 xl:flex-1 xl:grid-rows-[minmax(0,1fr)]',
          fit.mode === 'page'
            ? 'xl:grid-cols-[minmax(0,var(--doc-w))_minmax(300px,1fr)]'
            : 'xl:grid-cols-[minmax(0,5fr)_minmax(18rem,2fr)]',
        )}
        style={{ '--doc-w': fit.width ? `${fit.width}px` : '1fr' } as CSSProperties}
      >
        <div ref={fit.ref} className="scroll-thin min-w-0 xl:min-h-0 xl:overflow-y-auto">
          <DocumentViewer
            mode={canEdit ? 'review' : 'mirror'}
            pages={session.pages}
            fields={session.fields.map((field) => ({
              ...field,
              value: values.value(field.field_key, field.value),
            }))}
            checkboxGroups={session.checkbox_groups}
            signatures={session.signatures}
            currentPage={page}
            recentlyChanged={changed}
            onFieldChange={canEdit ? values.setValue : undefined}
            onCheckboxToggle={
              canEdit
                ? (groupKey, optionKey, selected) => {
                    const group = session.checkbox_groups.find((g) => g.group_key === groupKey)
                    if (!group) return
                    const current = group.options.filter((o) => o.selected).map((o) => o.option_key)
                    values.setCheckbox(
                      groupKey,
                      !selected
                        ? current.filter((key) => key !== optionKey)
                        : group.multi_select
                          ? [...new Set([...current, optionKey])]
                          : [optionKey],
                    )
                  }
                : undefined
            }
          />
        </div>

        <aside className="scroll-thin flex min-h-0 flex-col gap-3 xl:overflow-y-auto xl:pe-1">
          {session.status === 'draft' && (
            <Button
              variant="primary"
              icon={<Send className="h-4 w-4" />}
              loading={push.isPending}
              onClick={() => push.mutate()}
            >
              Send to {session.device?.name ?? 'tablet'}
            </Button>
          )}

          {awaiting && (
            <div className="rounded-[12px] border border-[var(--warning)] bg-[var(--warning-subtle)] p-3">
              <h3 className="text-[13px] font-semibold text-[var(--warning)]">
                Signed and submitted — ready for you to verify
              </h3>
              {issues.length > 0 ? (
                <ul className="mt-1.5 list-inside list-disc text-[12px] text-[var(--warning)]">
                  {issues.map((issue) => (
                    <li key={`${issue.kind}:${issue.key}`}>{issue.message}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-[12px] text-[var(--text-muted)]">
                  Nothing can be changed now. Check the details, then approve to finalize: the
                  signed PDF is sealed and locked, and a later correction becomes a new version.
                </p>
              )}
              {session.submitted_digest && (
                <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                  Submitted {session.submitted_at ? formatRelative(session.submitted_at) : ''} ·
                  fingerprint{' '}
                  <code className="tabular" title={session.submitted_digest}>
                    {session.submitted_digest.slice(0, 12)}
                  </code>
                </p>
              )}

              <div className="mt-3 flex flex-col gap-2">
                <a
                  href={`/api/v1/sessions/${session.id}/preview`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[12px] text-[var(--brand)] hover:underline"
                >
                  Open the preview of the signed document
                </a>
                <PermissionGate permission="sessions:approve">
                  <Button
                    variant="primary"
                    icon={<CheckCircle2 className="h-4 w-4" />}
                    loading={approve.isPending}
                    disabled={issues.length > 0}
                    onClick={() => approve.mutate()}
                  >
                    Approve &amp; save
                  </Button>
                </PermissionGate>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Undo2 className="h-3.5 w-3.5" />}
                  loading={reopen.isPending}
                  onClick={() => reopen.mutate()}
                >
                  Send back for a correction
                </Button>
              </div>
            </div>
          )}

          {session.status === 'failed' && (
            <div className="rounded-[12px] border border-[var(--danger)] bg-[var(--danger-subtle)] p-3">
              <h3 className="text-[13px] font-semibold text-[var(--danger)]">
                The document could not be saved
              </h3>
              <p className="mt-1 text-[12px]">{session.error_message}</p>
              <p className="mt-1 text-[12px] text-[var(--text-muted)]">
                The signature is safe. Press approve again once the problem is fixed — the guest
                does not need to sign twice.
              </p>
              <Button
                variant="secondary"
                size="sm"
                className="mt-2"
                icon={<RotateCcw className="h-3.5 w-3.5" />}
                loading={approve.isPending}
                onClick={() => approve.mutate()}
              >
                Try saving again
              </Button>
            </div>
          )}

          <MirrorFields
            session={session}
            value={values.value}
            onChange={values.setValue}
            saving={values.saving}
            readOnly={!canEdit}
          />

          <ChatPanel
            session={session}
            value={message}
            onChange={setMessage}
            sending={send.isPending}
            onSend={() => message.trim() && send.mutate(message.trim())}
          />

          <Separator />

          <Button
            variant="ghost"
            size="sm"
            icon={<Ban className="h-3.5 w-3.5" />}
            onClick={() => setConfirmCancel(true)}
            className="text-[var(--danger)]"
          >
            Cancel this session
          </Button>
        </aside>
      </div>

      <ConfirmDestructive
        open={confirmCancel}
        title="Cancel the signing session?"
        description="The guest's tablet returns to the signage screen and anything they have entered is discarded. The captured document stays in the inbox so you can start again."
        confirmLabel="Cancel session"
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() => {
          setConfirmCancel(false)
          cancel.mutate()
        }}
      />
    </Card>
  )
}

function ChatPanel({
  session,
  value,
  onChange,
  sending,
  onSend,
}: {
  session: SigningSession
  value: string
  onChange: (value: string) => void
  sending: boolean
  onSend: () => void
}) {
  const endRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' })
  }, [session.messages.length])

  return (
    // mt-auto pins the card to the bottom of the side panel, just above Cancel.
    <div className="flex shrink-0 flex-col rounded-[12px] border border-[var(--border)] xl:mt-auto">
      <div className="flex items-center gap-1.5 border-b border-[var(--border)] px-3 py-1.5 text-[12px] font-medium text-[var(--text-muted)]">
        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
        Message the guest
      </div>

      <div className="scroll-thin flex max-h-52 flex-col gap-1.5 overflow-y-auto px-3 py-2">
        {session.messages.length === 0 ? (
          <p className="text-[12px] text-[var(--text-muted)]">
            Tell the guest what to correct without walking round the counter.
          </p>
        ) : (
          session.messages.map((entry) => (
            <div
              key={entry.id}
              className={cn(
                'max-w-[85%] rounded-[10px] px-2.5 py-1.5 text-[12px]',
                entry.sender_type === 'device'
                  ? 'self-start bg-[var(--surface-2)]'
                  : 'self-end bg-[var(--brand-subtle)] text-[var(--brand)]',
              )}
            >
              {entry.body}
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <form
        className="flex gap-2 border-t border-[var(--border)] p-2"
        onSubmit={(event) => {
          event.preventDefault()
          onSend()
        }}
      >
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Type a message…"
          aria-label="Message to the guest"
        />
        <Button
          type="submit"
          size="sm"
          variant="secondary"
          loading={sending}
          disabled={!value.trim()}
        >
          Send
        </Button>
      </form>
    </div>
  )
}
